import React, { useState, useEffect } from 'react';
import BlockchainStatus from '../components/BlockchainStatus';
import TradingChart from '../components/TradingChart';
import {
  blockchainService,
  PurchaseRecord,
  ExchangeDistribution,
  SystemStatus,
  TreasuryStats,
  VaultStats,
  AuditStats,
  TokenInfo
} from '../services/blockchainService';
import { debugLog, config } from '../utils/config';
import { 
  getContractExplorerUrl, 
  getTransactionExplorerUrl, 
  getBWSTokenExplorerUrl,
  getLinkProps,
  getExplorerName 
} from '../utils/links';

interface ContractData {
  address: string;
  balance: string;
  lastTransaction: string;
  transactionCount: number;
  status: 'active' | 'paused' | 'error';
}

interface TransactionData {
  hash: string;
  type: 'deposit' | 'authorization' | 'purchase' | 'sale';
  amount: string;
  timestamp: Date;
  status: 'confirmed' | 'pending' | 'failed';
  gasUsed: string;
  blockNumber: number;
}

export const OnChainMonitor: React.FC = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [lastUpdate, setLastUpdate] = useState(new Date());
  const [recentPurchases, setRecentPurchases] = useState<PurchaseRecord[]>([]);
  const [exchangeDistribution, setExchangeDistribution] = useState<ExchangeDistribution[]>([]);
  const [isConnected, setIsConnected] = useState<boolean | null>(null);
  const [systemStatus, setSystemStatus] = useState<SystemStatus | null>(null);
  const [treasuryStats, setTreasuryStats] = useState<TreasuryStats | null>(null);
  const [vaultStats, setVaultStats] = useState<VaultStats | null>(null);
  const [auditStats, setAuditStats] = useState<AuditStats | null>(null);
  const [tokenInfo, setTokenInfo] = useState<TokenInfo | null>(null);
  const [recentTransactions, setRecentTransactions] = useState<TransactionData[]>([]);

  const fetchBlockchainData = async () => {
    try {
      setIsLoading(true);

      // Fetch system status first
      const status = await blockchainService.getSystemStatus();
      setSystemStatus(status);
      setIsConnected(status.connected);

      if (!status.connected) {
        debugLog('Not connected to blockchain');
        return;
      }

      // Fetch all contract data in parallel
      const promises: Promise<any>[] = [];

      // Treasury stats
      if (status.contractsDeployed.treasury) {
        promises.push(blockchainService.getTreasuryStats().catch(err => {
          debugLog('Failed to fetch treasury stats', err);
          return null;
        }));
      } else {
        promises.push(Promise.resolve(null));
      }

      // Vault stats
      if (status.contractsDeployed.vault) {
        promises.push(blockchainService.getVaultStats().catch(err => {
          debugLog('Failed to fetch vault stats', err);
          return null;
        }));
      } else {
        promises.push(Promise.resolve(null));
      }

      // Audit stats
      if (status.contractsDeployed.auditTrail) {
        promises.push(blockchainService.getAuditStats().catch(err => {
          debugLog('Failed to fetch audit stats', err);
          return null;
        }));
      } else {
        promises.push(Promise.resolve(null));
      }

      // Token info - Removed to avoid duplicate calls
      // BlockchainStatus component already fetches this data
      promises.push(Promise.resolve(null));

      // Recent purchases
      if (status.contractsDeployed.auditTrail) {
        promises.push(blockchainService.getRecentPurchases(10).catch(err => {
          debugLog('Failed to fetch recent purchases', err);
          return [];
        }));
      } else {
        promises.push(Promise.resolve([]));
      }

      // Exchange distribution
      if (status.contractsDeployed.auditTrail) {
        promises.push(blockchainService.getExchangeDistribution().catch(err => {
          debugLog('Failed to fetch exchange distribution', err);
          return [];
        }));
      } else {
        promises.push(Promise.resolve([]));
      }

      const [treasury, vault, audit, token, purchases, distribution] = await Promise.all(promises);

      setTreasuryStats(treasury);
      setVaultStats(vault);
      setAuditStats(audit);
      setTokenInfo(token);
      setRecentPurchases(purchases || []);
      setExchangeDistribution(distribution || []);

      // Convert purchase records to transaction format for display
      if (purchases && purchases.length > 0) {
        const transactions: TransactionData[] = purchases.map((purchase: PurchaseRecord) => ({
          hash: `0x${purchase.index.toString(16).padStart(64, '0')}`, // Generate a hash from index
          type: 'purchase' as const,
          amount: `${purchase.tokenAmount} BWS`,
          timestamp: new Date(purchase.timestamp * 1000),
          status: 'confirmed' as const,
          gasUsed: '0', // Not available from purchase record
          blockNumber: 0 // Not available from purchase record
        }));
        setRecentTransactions(transactions);
      }

      setLastUpdate(new Date());
    } catch (error) {
      debugLog('Error fetching blockchain data', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleRefresh = async () => {
    await fetchBlockchainData();
  };

  useEffect(() => {
    // Initial fetch
    fetchBlockchainData();

    // Set up periodic refresh - using very conservative interval to prevent 429 errors
    const interval = setInterval(fetchBlockchainData, config.intervals.slow); // Use slow interval

    // Disable event listeners for now to reduce API calls
    // Event listeners cause additional API calls that can trigger rate limits
    // Uncomment when using a paid Infura plan or local node
    /*
    if (isConnected) {
      blockchainService.setupEventListeners({
        onTokenPurchase: (vault, ethAmount, tokenAmount, exchange) => {
          debugLog('New token purchase detected', { vault, ethAmount, tokenAmount, exchange });
          fetchBlockchainData(); // Refresh data when new purchase occurs
        },
        onRevenueDeposited: (depositor, amount) => {
          debugLog('New revenue deposited', { depositor, amount });
          fetchBlockchainData();
        },
        onPurchaseAuthorized: (marketMaker, amount) => {
          debugLog('Purchase authorized', { marketMaker, amount });
          fetchBlockchainData();
        }
      });
    }
    */

    return () => {
      clearInterval(interval);
      // Event listeners are disabled for now
      // if (isConnected) {
      //   blockchainService.removeEventListeners();
      // }
    };
  }, [isConnected]);

  const getTransactionTypeColor = (type: TransactionData['type']) => {
    switch (type) {
      case 'purchase':
        return 'bg-success-subtle text-success';
      case 'sale':
        return 'bg-danger-subtle text-danger';
      case 'deposit':
        return 'bg-primary-subtle text-primary';
      case 'authorization':
        return 'bg-info-subtle text-info';
      default:
        return 'bg-secondary-subtle text-secondary';
    }
  };

  const getStatusColor = (status: TransactionData['status']) => {
    switch (status) {
      case 'confirmed':
        return 'text-success';
      case 'pending':
        return 'text-warning';
      case 'failed':
        return 'text-danger';
      default:
        return 'text-secondary';
    }
  };

  const formatTimestamp = (timestamp: number): string => {
    return new Date(timestamp * 1000).toLocaleString();
  };

  const formatNumber = (value: string, decimals: number = 4): string => {
    const num = parseFloat(value);
    if (isNaN(num)) return '0';
    return num.toLocaleString(undefined, { 
      minimumFractionDigits: 0, 
      maximumFractionDigits: decimals 
    });
  };

  return (
    <div className="container-fluid">
      {/* Page Header */}
      <div className="d-flex justify-content-between align-items-start mb-4">
        <div>
          <h1 className="h2 mb-2">On-Chain Monitor</h1>
          <p className="text-muted mb-1">
            Market maker system monitoring - Tracks automated BWS purchases using company revenue
          </p>
          <p className="text-muted small">
            Flow: Company Revenue → Treasury (ETH) → Market Maker → BWS Purchases → Vault Storage
          </p>
        </div>

        <button
          onClick={handleRefresh}
          disabled={isLoading || !isConnected}
          className="btn btn-outline-primary d-flex align-items-center gap-2"
        >
          <i className={`bi bi-arrow-clockwise ${isLoading ? 'spinner-border spinner-border-sm' : ''}`}></i>
          Refresh
        </button>
      </div>

      {/* Blockchain Status */}
      <div className="mb-4">
        <BlockchainStatus
          onStatusChange={setIsConnected}
          onDataFetched={(data) => {
            // Update local state with data from BlockchainStatus
            if (data.tokenInfo) setTokenInfo(data.tokenInfo);
            if (data.treasuryStats) setTreasuryStats(data.treasuryStats);
            if (data.vaultStats) setVaultStats(data.vaultStats);
            if (data.auditStats) setAuditStats(data.auditStats);
          }}
          refreshInterval={config.intervals.slow}
        />
      </div>

      {/* Trading Chart - Similar to GeckoTerminal */}
      <div className="mb-4">
        <TradingChart
          tokenAddress={config.blockchain.contracts.bwsToken}
          refreshInterval={config.intervals.medium}
        />
      </div>

      {/* Smart Contracts Status */}
      <div className="row g-3 mb-4">
        {/* Treasury Contract */}
        <div className="col-12 col-md-4">
          <div className="card shadow-sm h-100 border-primary">
            <div className="card-header bg-transparent">
              <div className="d-flex align-items-start gap-3">
                <i className="bi bi-shield-check fs-3 text-primary"></i>
                <div>
                  <h3 className="h6 fw-semibold mb-1">Treasury Contract</h3>
                  <p className="small text-muted mb-1">Company Revenue Management (NOT customer funds)</p>
                  <p className="text-muted" style={{ fontSize: '0.75rem' }}>Process flow: Company net revenue (FIAT) → Convert to ETH → Deposit to Treasury</p>
                  <p className="text-muted" style={{ fontSize: '0.75rem' }}>Function: Authorizes market maker vault to use ETH for BWS purchases</p>
                  <p className="text-muted" style={{ fontSize: '0.75rem' }}>Data: Treasury.getTreasuryStats() returns cumulative deposits & authorizations</p>
                </div>
              </div>
            </div>
            <div className="card-body">
              <div className="d-flex justify-content-between mb-3">
                <span className="text-muted">Status</span>
                {isLoading ? (
                  <span className="text-muted">-</span>
                ) : systemStatus?.contractsDeployed?.treasury ? (
                  <span className={`badge ${treasuryStats?.paused ? 'bg-warning' : 'bg-success'}`}>
                    {treasuryStats?.paused ? 'Paused' : 'Active'}
                  </span>
                ) : (
                  <span className="badge bg-danger">Not Deployed</span>
                )}
              </div>
              {treasuryStats && (
                <>
                  <div className="d-flex justify-content-between mb-2">
                    <span className="text-muted" title="Cumulative company net revenue deposited as ETH (Treasury.totalDeposited)">Total Revenue</span>
                    <span className="fw-medium">{formatNumber(treasuryStats.totalDeposited)} ETH</span>
                  </div>
                  <div className="d-flex justify-content-between mb-2">
                    <span className="text-muted" title="ETH authorized for market maker to purchase BWS tokens (Treasury.totalAuthorized)">Authorized</span>
                    <span className="fw-medium">{formatNumber(treasuryStats.totalAuthorized)} ETH</span>
                  </div>
                  <div className="d-flex justify-content-between mb-3">
                    <span className="text-muted">Available</span>
                    <span className="fw-medium">{formatNumber(treasuryStats.availableForAuthorization)} ETH</span>
                  </div>
                </>
              )}
              {!isLoading && !systemStatus?.contractsDeployed?.treasury && (
                <div className="small text-danger mb-3">
                  Contract address not configured
                </div>
              )}
              {isLoading && (
                <div className="mb-3">
                  <div className="d-flex justify-content-between mb-2">
                    <span className="text-muted">Total Revenue</span>
                    <span className="text-muted">-</span>
                  </div>
                  <div className="d-flex justify-content-between mb-2">
                    <span className="text-muted">Authorized</span>
                    <span className="text-muted">-</span>
                  </div>
                  <div className="d-flex justify-content-between mb-2">
                    <span className="text-muted">Available</span>
                    <span className="text-muted">-</span>
                  </div>
                </div>
              )}
              <div className="pt-3 border-top">
                {config.blockchain.contracts.treasury ? (
                  <a
                    {...getLinkProps(getContractExplorerUrl('treasury'))}
                    className="small text-primary text-decoration-none d-flex align-items-center gap-1"
                  >
                    <i className="bi bi-box-arrow-up-right"></i>
                    View on {getExplorerName()}
                  </a>
                ) : (
                  <span className="small text-muted">No address configured</span>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Market Making Vault */}
        <div className="col-12 col-md-4">
          <div className="card shadow-sm h-100 border-success">
            <div className="card-header bg-transparent">
              <div className="d-flex align-items-start gap-3">
                <i className="bi bi-database fs-3 text-success"></i>
                <div>
                  <h3 className="h6 fw-semibold mb-1">Market Making Vault</h3>
                  <p className="small text-muted mb-1">BWS Token Reserve (Market maker holdings, NOT customer tokens)</p>
                  <p className="text-muted" style={{ fontSize: '0.75rem' }}>Process: Receives ETH from Treasury → Purchases BWS → Holds for price stability</p>
                  <p className="text-muted" style={{ fontSize: '0.75rem' }}>Purpose: Maintains BWS liquidity & executes grid trading for price support</p>
                  <p className="text-muted" style={{ fontSize: '0.75rem' }}>Data: Vault.getVaultStats() tracks ETH received & BWS token balance</p>
                </div>
              </div>
            </div>
            <div className="card-body">
              <div className="d-flex justify-content-between mb-3">
                <span className="text-muted">Status</span>
                {isLoading ? (
                  <span className="text-muted">-</span>
                ) : systemStatus?.contractsDeployed?.vault ? (
                  <span className={`badge ${vaultStats?.paused ? 'bg-warning' : 'bg-success'}`}>
                    {vaultStats?.paused ? 'Paused' : 'Active'}
                  </span>
                ) : (
                  <span className="badge bg-danger">Not Deployed</span>
                )}
              </div>
              {vaultStats && (
                <>
                  <div className="d-flex justify-content-between mb-2">
                    <span className="text-muted" title="Cumulative ETH received from Treasury for BWS purchases (Vault.totalEthReceived)">ETH Received</span>
                    <span className="fw-medium">{formatNumber(vaultStats.totalEthReceived)} ETH</span>
                  </div>
                  <div className="d-flex justify-content-between mb-2">
                    <span className="text-muted" title="BWS tokens in vault for market making operations (Vault.totalTokensHeld)">BWS Held</span>
                    <span className="fw-medium">{formatNumber(vaultStats.totalTokensHeld)} BWS</span>
                  </div>
                  <div className="d-flex justify-content-between mb-3">
                    <span className="text-muted">Available</span>
                    <span className="fw-medium">{formatNumber(vaultStats.tokenUtilization.availableForWithdrawal)} BWS</span>
                  </div>
                </>
              )}
              {!isLoading && !systemStatus?.contractsDeployed?.vault && (
                <div className="small text-danger mb-3">
                  Contract address not configured
                </div>
              )}
              {isLoading && (
                <div className="mb-3">
                  <div className="d-flex justify-content-between mb-2">
                    <span className="text-muted">ETH Received</span>
                    <span className="text-muted">-</span>
                  </div>
                  <div className="d-flex justify-content-between mb-2">
                    <span className="text-muted">BWS Held</span>
                    <span className="text-muted">-</span>
                  </div>
                  <div className="d-flex justify-content-between mb-2">
                    <span className="text-muted">Available</span>
                    <span className="text-muted">-</span>
                  </div>
                </div>
              )}
              <div className="pt-3 border-top">
                {config.blockchain.contracts.vault ? (
                  <a
                    {...getLinkProps(getContractExplorerUrl('vault'))}
                    className="small text-primary text-decoration-none d-flex align-items-center gap-1"
                  >
                    <i className="bi bi-box-arrow-up-right"></i>
                    View on {getExplorerName()}
                  </a>
                ) : (
                  <span className="small text-muted">No address configured</span>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Audit Trail */}
        <div className="col-12 col-md-4">
          <div className="card shadow-sm h-100 border-info">
            <div className="card-header bg-transparent">
              <div className="d-flex align-items-start gap-3">
                <i className="bi bi-file-earmark-text fs-3 text-info"></i>
                <div>
                  <h3 className="h6 fw-semibold mb-1">Audit Trail</h3>
                  <p className="small text-muted mb-1">Immutable Market Maker Purchase History (NOT customer trades)</p>
                  <p className="text-muted" style={{ fontSize: '0.75rem' }}>Records: Each BWS purchase by market maker from exchanges</p>
                  <p className="text-muted" style={{ fontSize: '0.75rem' }}>Data stored: Exchange name, ETH spent, BWS received, timestamp, tx hash</p>
                  <p className="text-muted" style={{ fontSize: '0.75rem' }}>Process: After each purchase → vault.recordPurchase() → Permanent on-chain log</p>
                </div>
              </div>
            </div>
            <div className="card-body">
              <div className="d-flex justify-content-between mb-3">
                <span className="text-muted">Status</span>
                {isLoading ? (
                  <span className="text-muted">-</span>
                ) : systemStatus?.contractsDeployed?.auditTrail ? (
                  <span className="badge bg-success">Active</span>
                ) : (
                  <span className="badge bg-danger">Not Deployed</span>
                )}
              </div>
              {auditStats && (
                <>
                  <div className="d-flex justify-content-between mb-2">
                    <span className="text-muted" title="Total market maker BWS purchases from DEXs/CEXs (AuditTrail.totalPurchases)">Total Purchases</span>
                    <span className="fw-medium">{auditStats.totalPurchases.toLocaleString()}</span>
                  </div>
                  <div className="d-flex justify-content-between mb-2">
                    <span className="text-muted" title="Total ETH spent by market maker on BWS token purchases (AuditTrail.totalEthSpent)">ETH Spent</span>
                    <span className="fw-medium">{formatNumber(auditStats.totalEthSpent)} ETH</span>
                  </div>
                  <div className="d-flex justify-content-between mb-3">
                    <span className="text-muted" title="Total BWS tokens acquired by market maker from exchanges (AuditTrail.totalTokensPurchased)">BWS Purchased</span>
                    <span className="fw-medium">{formatNumber(auditStats.totalTokensPurchased)} BWS</span>
                  </div>
                </>
              )}
              {!isLoading && !systemStatus?.contractsDeployed?.auditTrail && (
                <div className="small text-danger mb-3">
                  Contract address not configured
                </div>
              )}
              {isLoading && (
                <div className="mb-3">
                  <div className="d-flex justify-content-between mb-2">
                    <span className="text-muted">Total Purchases</span>
                    <span className="text-muted">-</span>
                  </div>
                  <div className="d-flex justify-content-between mb-2">
                    <span className="text-muted">ETH Spent</span>
                    <span className="text-muted">-</span>
                  </div>
                  <div className="d-flex justify-content-between mb-2">
                    <span className="text-muted">BWS Purchased</span>
                    <span className="text-muted">-</span>
                  </div>
                </div>
              )}
              <div className="pt-3 border-top">
                {config.blockchain.contracts.auditTrail ? (
                  <a
                    {...getLinkProps(getContractExplorerUrl('auditTrail'))}
                    className="small text-primary text-decoration-none d-flex align-items-center gap-1"
                  >
                    <i className="bi bi-box-arrow-up-right"></i>
                    View on {getExplorerName()}
                  </a>
                ) : (
                  <span className="small text-muted">No address configured</span>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Recent Transactions */}
      <div className="card shadow-sm mb-4">
        <div className="card-header bg-transparent">
          <h2 className="h5 fw-semibold mb-1">Recent Market Maker Transactions</h2>
          <p className="small text-muted mb-1">Automated BWS token purchases using company revenue (NOT customer purchases)</p>
          <p className="text-muted" style={{ fontSize: '0.75rem' }}>Process: Treasury receives company net revenue → Authorizes market maker → Executes BWS buys on exchanges</p>
          <p className="text-muted" style={{ fontSize: '0.75rem' }}>Shows last 10 purchases from DEXs (Uniswap, Sushiswap) and CEXs (Coinbase, Binance)</p>
          <p className="text-muted" style={{ fontSize: '0.75rem' }}>Data source: AuditTrail.getPurchaseRecord() retrieves immutable on-chain records</p>
        </div>
        <div className="card-body">
          <div className="table-responsive">
            <table className="table table-hover align-middle mb-0">
              <thead className="table-light">
                <tr>
                  <th>Transaction Hash</th>
                  <th>Type</th>
                  <th>Amount</th>
                  <th>Status</th>
                  <th>Gas Used</th>
                  <th>Block</th>
                  <th>Time</th>
                </tr>
              </thead>
              <tbody>
                {recentTransactions.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="text-center text-muted py-5">
                      <div>
                        <p className="fw-medium mb-2">No market maker transactions recorded yet</p>
                        <p className="small mb-3">Automated BWS token purchases from exchanges will appear here</p>
                        <p className="mb-1" style={{ fontSize: '0.75rem' }}>Complete purchase process flow:</p>
                        <p className="text-muted mb-0" style={{ fontSize: '0.75rem' }}>1. Company deposits net revenue (FIAT→ETH) to Treasury contract</p>
                        <p className="text-muted mb-0" style={{ fontSize: '0.75rem' }}>2. Treasury authorizes market maker vault with ETH allocation</p>
                        <p className="text-muted mb-0" style={{ fontSize: '0.75rem' }}>3. Market maker algorithms analyze price/liquidity across exchanges</p>
                        <p className="text-muted mb-0" style={{ fontSize: '0.75rem' }}>4. Executes BWS buy orders via DEX routers or CEX APIs</p>
                        <p className="text-muted mb-0" style={{ fontSize: '0.75rem' }}>5. Records purchase details immutably in AuditTrail contract</p>
                        <p className="text-muted mb-0" style={{ fontSize: '0.75rem' }}>6. Transfers purchased BWS tokens to vault for price support</p>
                      </div>
                    </td>
                  </tr>
                ) : (
                  recentTransactions.map((tx) => (
                  <tr key={tx.hash}>
                    <td>
                      <a
                        {...getLinkProps(getTransactionExplorerUrl(tx.hash))}
                        className="text-primary text-decoration-none d-flex align-items-center gap-1"
                      >
                        {tx.hash}
                        <i className="bi bi-box-arrow-up-right" style={{ fontSize: '0.75rem' }}></i>
                      </a>
                    </td>
                    <td>
                      <span className={`badge ${getTransactionTypeColor(tx.type)}`}>
                        {tx.type.charAt(0).toUpperCase() + tx.type.slice(1)}
                      </span>
                    </td>
                    <td className="fw-medium">{tx.amount}</td>
                    <td>
                      <span className={`fw-medium ${getStatusColor(tx.status)}`}>
                        {tx.status.charAt(0).toUpperCase() + tx.status.slice(1)}
                      </span>
                    </td>
                    <td>{tx.gasUsed}</td>
                    <td>{tx.blockNumber.toLocaleString()}</td>
                    <td className="small text-muted">
                      {tx.timestamp.toLocaleTimeString()}
                    </td>
                  </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

    </div>
  );
};